import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faFileCode, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import moment from 'moment';
import BZAPIService from '../../services/BZAPIService';
import {
  reportTypeLabel,
  projectsById,
  projectLabel,
  canDownload,
  statusPillClass,
  filterRows,
  filterOptions
} from '../bugscrub/reportRows';

/**
 * Plan Reports (STRY0038480) — every export across every project, newest
 * first, filterable by project and status.
 *
 * The listing API already returns all jobs when unfiltered and already sorts
 * createdAt desc, so this page makes one unfiltered call and filters in state.
 * Presigned URLs are minted per row on click (never in bulk for a tenant-wide
 * list), which is the same mechanism ExportModal uses.
 *
 * Read-only: it carries no permission gate of its own beyond the route's
 * <ProductGuard product="plan">, matching the fact that viewing a project's
 * export history is already ungated.
 */
const PlanReports = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectFilter, setProjectFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [downloadingJobId, setDownloadingJobId] = useState(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const [exportsResponse, projectList] = await Promise.all([
        BZAPIService.bsaListExports({}),
        BZAPIService.bsaListProjects()
      ]);
      setRows(exportsResponse?.valid && exportsResponse?.jobs ? exportsResponse.jobs : []);
      setProjects(Array.isArray(projectList) ? projectList : []);
    } catch (error) {
      console.error('Failed to load reports:', error);
      setLoadFailed(true);
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const byId = useMemo(() => projectsById(projects), [projects]);
  const options = useMemo(() => filterOptions(rows, byId), [rows, byId]);
  const visible = useMemo(() => filterRows(rows, { projectId: projectFilter, status: statusFilter }), [
    rows,
    projectFilter,
    statusFilter
  ]);

  const handleDownload = async (jobId, format) => {
    try {
      setDownloadingJobId(jobId);
      // The list response withholds presigned URLs on purpose; re-fetch the
      // single job to mint a short-lived one for just this row.
      const response = await BZAPIService.bsaListExports({ jobId });
      if (!response?.valid || !response?.job) {
        toast.error('Failed to get download URL');
        return;
      }
      const url = format === 'pdf' ? response.job.pdfUrl : response.job.htmlUrl;
      if (!url) {
        toast.error(`${format.toUpperCase()} file is not available yet`);
        return;
      }
      window.open(url, '_blank');
    } catch (error) {
      console.error('Failed to download:', error);
      toast.error(`Failed to download ${format.toUpperCase()}`);
    } finally {
      setDownloadingJobId(null);
    }
  };

  const renderProject = row => {
    const { name, linkable } = projectLabel(row, byId);
    if (!linkable) {
      // The project is gone — deleting a project mid-export is how a failed
      // job gets produced — so don't link into a dead route.
      return <span className="bz-muted">{name}</span>;
    }
    return <Link to={`/plan/${row.projectId}`}>{name}</Link>;
  };

  const emptyMessage =
    projectFilter || statusFilter
      ? 'No exports match the current filters.'
      : 'No reports have been exported yet. Open a project and use Export Report to create one.';

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1320, margin: '0 auto' }}>
      <div className="bz-section-head" style={{ marginBottom: 24 }}>
        <div>
          <div className="bz-kicker" style={{ color: 'var(--plan)' }}>
            Plan · Reports
          </div>
          <h1 className="bz-h1" style={{ marginTop: 4 }}>
            Reports
          </h1>
          <div className="bz-muted" style={{ fontSize: 'var(--fs-md)', marginTop: 4 }}>
            Every report exported across your Plan projects, newest first.
          </div>
        </div>
        <div className="bz-row" style={{ gap: 8 }}>
          <button type="button" className="bz-btn bz-btn--sm" onClick={load} disabled={isLoading}>
            <FontAwesomeIcon icon={faRefresh} className={isLoading ? 'fa-spin' : ''} />
            {' Refresh'}
          </button>
        </div>
      </div>

      <div className="bz-card">
        <div className="bz-row" style={{ gap: 16, padding: '12px 16px', borderBottom: '1px solid var(--line-soft)' }}>
          <div className="bz-row" style={{ gap: 8 }}>
            <label className="bz-muted" htmlFor="report-project-filter">
              Project
            </label>
            <span className="bz-input">
              <select id="report-project-filter" value={projectFilter} onChange={e => setProjectFilter(e.target.value)}>
                <option value="">All projects</option>
                {options.projects.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </span>
          </div>

          <div className="bz-row" style={{ gap: 8 }}>
            <label className="bz-muted" htmlFor="report-status-filter">
              Status
            </label>
            <span className="bz-input">
              <select id="report-status-filter" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="">All statuses</option>
                {options.statuses.map(s => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </span>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center" style={{ padding: 40 }}>
            <Spinner color="primary" />
            <div className="bz-muted" style={{ marginTop: 8 }}>
              Loading reports…
            </div>
          </div>
        ) : loadFailed ? (
          <div className="text-center" style={{ padding: 40 }}>
            <div style={{ fontWeight: 600 }}>Failed to load reports</div>
            <div className="bz-muted" style={{ marginTop: 4 }}>
              Try Refresh. If it keeps failing, the export service may be unavailable.
            </div>
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center" style={{ padding: 40 }}>
            <div className="bz-muted">{emptyMessage}</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="bz-table">
              <thead>
                <tr>
                  <th>Report name</th>
                  <th>Project</th>
                  <th>Type</th>
                  <th>Requested by</th>
                  <th>Created (UTC)</th>
                  <th>Status</th>
                  <th>Download</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(row => (
                  <tr key={row.jobId}>
                    <td>{row.reportName}</td>
                    <td>{renderProject(row)}</td>
                    <td>{reportTypeLabel(row.reportType)}</td>
                    <td>{row.author}</td>
                    <td className="bz-mono">{moment.utc(row.createdAt).format('YYYY-MM-DD HH:mm')}</td>
                    <td>
                      <span className={statusPillClass(row.status)}>{row.status}</span>
                      {row.errorMessage && (
                        <div
                          className="bz-muted"
                          style={{ color: 'var(--bad)', fontSize: 'var(--fs-sm)', marginTop: 4 }}
                        >
                          {row.errorMessage}
                        </div>
                      )}
                    </td>
                    <td>
                      {canDownload(row) ? (
                        <div className="bz-row" style={{ gap: 8 }}>
                          <button
                            type="button"
                            className="bz-btn bz-btn--sm"
                            onClick={() => handleDownload(row.jobId, 'pdf')}
                            disabled={downloadingJobId === row.jobId}
                            title="Download PDF"
                          >
                            {downloadingJobId === row.jobId ? (
                              <Spinner size="sm" />
                            ) : (
                              <FontAwesomeIcon icon={faFilePdf} />
                            )}
                            {' PDF'}
                          </button>
                          <button
                            type="button"
                            className="bz-btn bz-btn--sm"
                            onClick={() => handleDownload(row.jobId, 'html')}
                            disabled={downloadingJobId === row.jobId}
                            title="Download HTML"
                          >
                            {downloadingJobId === row.jobId ? (
                              <Spinner size="sm" />
                            ) : (
                              <FontAwesomeIcon icon={faFileCode} />
                            )}
                            {' HTML'}
                          </button>
                        </div>
                      ) : (
                        <span className="bz-muted">Not ready</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanReports;
